import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import closeIcon from '../assets/close.svg';

const ImageModal = ({ isOpen, onClose, images, title }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  
  // Handle ESC key to close modal or lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (isLightboxOpen) {
          setIsLightboxOpen(false);
        } else {
          onClose();
        }
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose, isLightboxOpen]);
  
  // Prevent body scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);
  
  // Click outside to close
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      if (isLightboxOpen) {
        setIsLightboxOpen(false);
      } else {
        onClose();
      }
    }
  };

  const openLightbox = (image) => {
    setSelectedImage(image);
    setIsLightboxOpen(true);
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <>
      <div style={modalStyles.overlay} onClick={handleOverlayClick}>
        <div style={modalStyles.container}>
          <div style={modalStyles.header}>
            <h2 style={modalStyles.title}>{title}</h2>
            <img 
              src={closeIcon} 
              alt="Close" 
              onClick={onClose} 
              style={modalStyles.closeButton} 
            />
          </div>
          
          <div style={modalStyles.imageGridContainer}>
            <div style={modalStyles.imageGrid}>
              {images.map((image, index) => (
                <div 
                  key={index} 
                  style={modalStyles.imageCard}
                  onClick={() => openLightbox(image)}
                >
                  <div 
                    style={modalStyles.imageWrapper}
                    className="image-hover-effect"
                  >
                    <img
                      src={image?.imageUrl || image?.thumbnailUrl}
                      alt={image?.title || 'Image'}
                      style={modalStyles.image}
                      loading="lazy"
                    />
                    {image.title && (
                      <div style={modalStyles.imageCaption}>
                        {image.title}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {images.length > 4 && (
            <div style={modalStyles.footer}>
              <span style={modalStyles.imageCount}>
                {images.length} images
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Lightbox for full-size image view */}
      {isLightboxOpen && selectedImage && (
        <div style={lightboxStyles.overlay} onClick={handleOverlayClick}>
          <div style={lightboxStyles.container}>
            <img 
              src={closeIcon} 
              alt="Close" 
              onClick={() => setIsLightboxOpen(false)} 
              style={lightboxStyles.closeButton} 
            />
            
            <div style={lightboxStyles.imageContainer}>
              <img
                src={selectedImage?.imageUrl || selectedImage?.thumbnailUrl}
                alt={selectedImage?.title || 'Image'}
                style={lightboxStyles.image}
              />
            </div>
            
            {selectedImage.title && (
              <div style={lightboxStyles.caption}>
                {selectedImage.title}
              </div>
            )}
            
            {selectedImage.link && (
              <a 
                href={selectedImage.link}
                target="_blank"
                rel="noopener noreferrer"
                style={lightboxStyles.visitButton}
              >
                Visit Source
              </a>
            )}
          </div>
        </div>
      )}

      {/* Add CSS for hover effects */}
      {isOpen && addStyles()}
    </>,
    document.body
  );
};

// Function to add styles to document head
const addStyles = () => {
  if (typeof document !== 'undefined' && !document.getElementById('image-modal-styles')) {
    const style = document.createElement('style');
    style.id = 'image-modal-styles';
    style.innerHTML = `
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      
      @keyframes slideUp {
        from { 
          transform: translateY(20px);
          opacity: 0;
        }
        to { 
          transform: translateY(0);
          opacity: 1;
        }
      }
      
      @keyframes zoomIn {
        from { 
          transform: scale(0.95);
          opacity: 0;
        }
        to { 
          transform: scale(1);
          opacity: 1;
        }
      }
      
      .image-hover-effect {
        transition: transform 0.3s ease, box-shadow 0.3s ease;
      }
      
      .image-hover-effect:hover {
        transform: translateY(-5px);
        box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
      }
    `;
    document.head.appendChild(style);
  }
};

const modalStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    backdropFilter: 'blur(5px)',
    animation: 'fadeIn 0.3s ease-out',
  },
  container: {
    width: '85%',
    maxWidth: '900px',
    maxHeight: '85vh',
    background: 'rgba(250, 250, 250, 0.95)',
    borderRadius: '16px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
    position: 'relative',
    overflowY: 'hidden',
    backdropFilter: 'blur(10px)',
    fontFamily: "'Poppins', sans-serif",
    border: '1px solid rgba(255, 255, 255, 0.3)',
    display: 'flex',
    flexDirection: 'column',
    animation: 'slideUp 0.4s ease-out',
  },
  header: {
    padding: '16px 24px',
    borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: 'rgba(255, 255, 255, 0.5)',
    backdropFilter: 'blur(10px)',
    borderTopLeftRadius: '16px',
    borderTopRightRadius: '16px',
    position: 'sticky',
    top: '0',
    zIndex: 10,
  },
  closeButton: {
    width: '24px',
    height: '24px',
    cursor: 'pointer',
    transition: 'transform 0.2s ease, opacity 0.2s ease',
    opacity: 0.7,
  },
  title: {
    fontSize: '18px',
    fontWeight: '600',
    margin: 0,
    color: '#111',
    textAlign: 'left',
    fontFamily: "'Poppins', sans-serif",
    flex: 1,
  },
  imageGridContainer: {
    padding: '16px',
    overflowY: 'auto',
    maxHeight: 'calc(85vh - 120px)',
  },
  imageGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: '16px',
    padding: '8px',
  },
  imageCard: {
    borderRadius: '8px',
    overflow: 'hidden',
    cursor: 'pointer',
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
    transition: 'transform 0.3s ease',
  },
  imageWrapper: {
    position: 'relative',
    overflow: 'hidden',
    borderRadius: '8px',
    aspectRatio: '4/3',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'transform 0.3s ease',
  },
  imageCaption: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    padding: '8px 12px',
    background: 'rgba(0, 0, 0, 0.6)',
    color: 'white',
    fontSize: '12px',
    fontWeight: '500',
    textAlign: 'center',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    backdropFilter: 'blur(2px)',
  },
  footer: {
    padding: '12px 24px',
    borderTop: '1px solid rgba(0, 0, 0, 0.1)',
    display: 'flex',
    justifyContent: 'flex-end',
    background: 'rgba(245, 245, 245, 0.5)',
  },
  imageCount: {
    fontSize: '14px',
    color: '#666',
    fontWeight: '500',
  }
};

const lightboxStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1500,
    backdropFilter: 'blur(8px)',
    animation: 'fadeIn 0.3s ease-out',
  },
  container: {
    width: '90%',
    maxWidth: '1200px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    animation: 'zoomIn 0.3s ease-out',
  },
  closeButton: {
    position: 'absolute',
    top: '-40px',
    right: '0',
    width: '24px',
    height: '24px',
    cursor: 'pointer',
    filter: 'invert(1)',
    transition: 'transform 0.2s ease',
    opacity: 0.8,
  },
  imageContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    maxHeight: '80vh',
    overflow: 'hidden',
  },
  image: {
    maxWidth: '100%',
    maxHeight: '80vh',
    objectFit: 'contain',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
    borderRadius: '4px',
  },
  caption: {
    color: 'white',
    fontSize: '16px',
    fontWeight: '500',
    textAlign: 'center',
    padding: '16px 0',
    maxWidth: '80%',
    margin: '0 auto',
  },
  visitButton: {
    display: 'block',
    margin: '8px auto 0',
    padding: '8px 16px',
    background: 'rgba(255, 255, 255, 0.15)',
    color: 'white',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: '500',
    textDecoration: 'none',
    textAlign: 'center',
    width: 'fit-content',
    cursor: 'pointer',
    transition: 'background 0.2s ease, transform 0.2s ease',
  }
};

export default ImageModal;